import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Badge, Card, Alert } from "react-bootstrap";
import {
  FaPlus,
  FaTrash,
  FaArrowUp,
  FaArrowDown,
  FaArrowRight,
  FaArrowLeft,
  FaClone,
  FaEdit,
  FaEye,
  FaStar,
  FaCog,
  FaFilter,
  FaSort,
  FaGripVertical,
  FaSearch,
  FaTimes,
  FaQuestionCircle
} from "react-icons/fa";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import { getUserFieldPermissionsByObject, getFlsReadDeniedLowerSet } from "../../utils/permissionCache";
import "./css/EnhancedListViewDialog.css";

function listViewFiltersFlsSafe(rawFilters, readDenied) {
  if (!Array.isArray(rawFilters)) return [];
  return rawFilters.filter((f) => !f?.field || !readDenied.has(String(f.field).toLowerCase()));
}

function kanbanSummaryFlsSafe(raw, readDenied) {
  if (!Array.isArray(raw)) return [];
  return raw.filter((sf) => !sf?.field || !readDenied.has(String(sf.field).toLowerCase()));
}

const EnhancedListViewDialog = ({
  show,
  onClose,
  onSave,
  onUpdate,
  selectedFieldList,
  objectName,
  editMode = false,
  listViewData = null,
  currentPlatform = "web", // Accept current platform from parent
}) => {
  const [listLabel, setListLabel] = useState("");
  const [listName, setListName] = useState("");
  const [selectedFields, setSelectedFields] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [sortBy, setSortBy] = useState("createddate");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [isDefault, setIsDefault] = useState(false);
  const [platform, setPlatform] = useState(currentPlatform);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isNameManuallyEdited, setIsNameManuallyEdited] = useState(false); // Track if user manually edited name

  // Filter states
  const [filters, setFilters] = useState([]);
  const [filterLogic, setFilterLogic] = useState("AND");
  const [showFilterSection, setShowFilterSection] = useState(false);
  const [showDateHelpModal, setShowDateHelpModal] = useState(false);

  // Drag and drop states
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Search state for available fields
  const [fieldSearchTerm, setFieldSearchTerm] = useState("");

  // Kanban configuration states
  const [viewType, setViewType] = useState('list'); // 'list' or 'kanban'
  const [kanbanGroupByField, setKanbanGroupByField] = useState('');
  const [kanbanCardFields, setKanbanCardFields] = useState([]);
  const [kanbanColorField, setKanbanColorField] = useState('');
  const [kanbanSortBy, setKanbanSortBy] = useState('');
  const [kanbanSortOrder, setKanbanSortOrder] = useState('asc');
  const [kanbanShowRecordCount, setKanbanShowRecordCount] = useState(true);

  // Kanban card field selection states
  const [kanbanAvailableFields, setKanbanAvailableFields] = useState([]);
  const [kanbanSelectedFields, setKanbanSelectedFields] = useState([]);
  const [kanbanFieldSearchTerm, setKanbanFieldSearchTerm] = useState("");
  const [kanbanDraggedItem, setKanbanDraggedItem] = useState(null);
  const [kanbanDragOverItem, setKanbanDragOverItem] = useState(null);

  // Kanban summary fields states
  const [kanbanSummaryFields, setKanbanSummaryFields] = useState([]);

  useEffect(() => {
    async function fetchFields() {
      try {
        setLoading(true);
        setError("");

        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        const result = response.data;
        const rawConfig = result?.config;
        const config = typeof rawConfig === 'string' ? (() => { try { return JSON.parse(rawConfig); } catch { return {}; } })() : (rawConfig || {});
        const hiddenSet = new Set(((config.hidden_fields || [])).map(f => (f || '').toLowerCase()).filter(Boolean));

        let flsByObj = {};
        try {
          flsByObj = await getUserFieldPermissionsByObject();
        } catch {
          flsByObj = {};
        }
        const readDenied = getFlsReadDeniedLowerSet(flsByObj, objectName);

        if (result?.fields?.length > 0) {
          const allFields = result.fields
            .filter((f) => !hiddenSet.has((f.field_name || '').toLowerCase()))
            .filter((f) => !readDenied.has(String(f.field_name || '').toLowerCase()))
            .map((f) => ({
              id: f.id,
              label: f.field_label,
              name: f.field_name,
              dataType: f.data_type || 'text',
            }));

          let selected = [];

          if (editMode && listViewData) {
            // Edit mode: populate with existing data
            setListLabel(listViewData.label || "");
            setListName(listViewData.name || "");
            setIsDefault(listViewData.is_default || false);
            setPlatform(listViewData.platform || "web");

            if (listViewData.config) {
              setSortBy(listViewData.config.sortBy || "createddate");
              setSortOrder(listViewData.config.sortOrder || "DESC");

              // Map existing fields - preserve order from config
              const existingFieldNames = listViewData.config.fields || [];
              selected = existingFieldNames.map(fieldName =>
                allFields.find(f => f.name === fieldName)
              ).filter(Boolean); // Remove any undefined fields

              // Load existing filters
              if (listViewData.config.filters && listViewData.config.filters.length > 0) {
                const safeF = listViewFiltersFlsSafe(listViewData.config.filters, readDenied);
                setFilters(safeF);
                setShowFilterSection(safeF.length > 0);
              }

              // Load filter logic
              if (listViewData.config.logic) {
                setFilterLogic(listViewData.config.logic);
              }

              // Load Kanban configuration
              if (listViewData.config.viewType) {
                setViewType(listViewData.config.viewType);
              }
              if (listViewData.config.kanban) {
                setKanbanGroupByField(listViewData.config.kanban.groupByField || '');

                // Load card fields and separate into selected and available
                const cardFieldNames = listViewData.config.kanban.cardFields || [];
                const selectedCardFields = cardFieldNames
                  .map(fieldName => allFields.find(f => f.name === fieldName))
                  .filter(Boolean);
                const availableCardFields = allFields.filter(
                  f => !cardFieldNames.includes(f.name)
                );

                setKanbanSelectedFields(selectedCardFields);
                setKanbanAvailableFields(availableCardFields);
                setKanbanCardFields(cardFieldNames);

                setKanbanColorField(listViewData.config.kanban.colorField || '');
                setKanbanSortBy(listViewData.config.kanban.sortBy || '');
                setKanbanSortOrder(listViewData.config.kanban.sortOrder || 'asc');
                setKanbanShowRecordCount(listViewData.config.kanban.showRecordCount !== false);
                setKanbanSummaryFields(
                  kanbanSummaryFlsSafe(listViewData.config.kanban.summaryFields || [], readDenied)
                );
              } else {
                // Initialize with all fields as available
                setKanbanAvailableFields(allFields);
                setKanbanSelectedFields([]);
                setKanbanSummaryFields([]);
              }
            }
          } else if (!editMode && listViewData) {
            // Clone mode: populate with cloned data (create mode but with data)
            setListLabel(listViewData.label || "");
            setListName(listViewData.name || "");
            setIsDefault(listViewData.is_default || false);
            setPlatform(listViewData.platform || currentPlatform);
            setIsNameManuallyEdited(false); // Allow name to be edited

            if (listViewData.config) {
              setSortBy(listViewData.config.sortBy || "createddate");
              setSortOrder(listViewData.config.sortOrder || "DESC");

              // Map existing fields - preserve order from config
              const existingFieldNames = listViewData.config.fields || [];
              selected = existingFieldNames.map(fieldName =>
                allFields.find(f => f.name === fieldName)
              ).filter(Boolean); // Remove any undefined fields

              // Load existing filters
              if (listViewData.config.filters && listViewData.config.filters.length > 0) {
                const safeF = listViewFiltersFlsSafe(listViewData.config.filters, readDenied);
                setFilters(safeF);
                setShowFilterSection(safeF.length > 0);
              }

              // Load filter logic
              if (listViewData.config.logic) {
                setFilterLogic(listViewData.config.logic);
              }

              // Load Kanban configuration
              if (listViewData.config.viewType) {
                setViewType(listViewData.config.viewType);
              }
              if (listViewData.config.kanban) {
                setKanbanGroupByField(listViewData.config.kanban.groupByField || '');

                // Load card fields and separate into selected and available
                const cardFieldNames = listViewData.config.kanban.cardFields || [];
                const selectedCardFields = cardFieldNames
                  .map(fieldName => allFields.find(f => f.name === fieldName))
                  .filter(Boolean);
                const availableCardFields = allFields.filter(
                  f => !cardFieldNames.includes(f.name)
                );

                setKanbanSelectedFields(selectedCardFields);
                setKanbanAvailableFields(availableCardFields);
                setKanbanCardFields(cardFieldNames);

                setKanbanColorField(listViewData.config.kanban.colorField || '');
                setKanbanSortBy(listViewData.config.kanban.sortBy || '');
                setKanbanSortOrder(listViewData.config.kanban.sortOrder || 'asc');
                setKanbanShowRecordCount(listViewData.config.kanban.showRecordCount !== false);
                setKanbanSummaryFields(
                  kanbanSummaryFlsSafe(listViewData.config.kanban.summaryFields || [], readDenied)
                );
              } else {
                // Initialize with all fields as available
                setKanbanAvailableFields(allFields);
                setKanbanSelectedFields([]);
                setKanbanSummaryFields([]);
              }
            }
          } else {
            // Create mode: default to ID field
            selected = allFields.find((f) => f.name === "id") ? [allFields.find((f) => f.name === "id")] : [];

            // Initialize Kanban fields with all fields as available
            setKanbanAvailableFields(allFields);
            setKanbanSelectedFields([]);
            setKanbanSummaryFields([]);
          }

          const remaining = allFields.filter(
            (f) => !selected.some((sf) => sf.name === f.name)
          );

          setAvailableFields(remaining);
          setSelectedFields(selected);

          const fieldStillExists = (nm) => nm && allFields.some((f) => f.name === nm);
          setKanbanGroupByField((prev) => (fieldStillExists(prev) ? prev : ''));
          setKanbanColorField((prev) => (fieldStillExists(prev) ? prev : ''));
          setKanbanSortBy((prev) => (fieldStillExists(prev) ? prev : ''));
          setSortBy((prev) => {
            const allowed = new Set(selected.map((s) => s.name));
            if (allowed.has(prev)) return prev;
            return selected[0]?.name || 'createddate';
          });
        }
      } catch (err) {
        console.error("Failed to fetch object fields:", err);
        setError("Failed to load fields. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    if (show) {
      fetchFields();
      if (!editMode) {
        setListLabel("");
        setListName("");
        setIsDefault(false);
        setPlatform(currentPlatform);
        setSortBy("createddate");
        setSortOrder("DESC");
        setIsNameManuallyEdited(false); // Reset manual edit flag
      }
    }
  }, [show, editMode, listViewData, objectName, currentPlatform]);

  const getMaxFields = () => {
    return platform === 'mobile' ? 8 : 15;
  };

  const moveToSelected = (field) => {
    const maxFields = getMaxFields();

    if (selectedFields.length >= maxFields) {
      setError(`Maximum ${maxFields} fields allowed for ${platform} platform`);
      return;
    }

    const newFields = [...selectedFields, field];

    setSelectedFields(newFields);
    setAvailableFields(availableFields.filter((f) => f !== field));
    if (error.includes('Maximum')) {
      setError("");
    }
  };

  const moveToAvailable = (field) => {
    setAvailableFields([...availableFields, field]);
    setSelectedFields(selectedFields.filter((f) => f !== field));
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newFields = [...selectedFields];
    [newFields[index - 1], newFields[index]] = [
      newFields[index],
      newFields[index - 1],
    ];
    setSelectedFields(newFields);
  };

  const moveDown = (index) => {
    if (index === selectedFields.length - 1) return;
    const newFields = [...selectedFields];
    [newFields[index + 1], newFields[index]] = [
      newFields[index],
      newFields[index + 1],
    ];
    setSelectedFields(newFields);
  };

  // Drag and drop functions
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem !== null && draggedItem !== index) {
      setDragOverItem(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const newFields = [...selectedFields];
    const draggedField = newFields[draggedItem];

    // Remove the dragged item
    newFields.splice(draggedItem, 1);

    // Insert at the new position
    newFields.splice(index, 0, draggedField);

    setSelectedFields(newFields);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleLabelChange = (e) => {
    const value = e.target.value;
    setListLabel(value);
    // Auto-generate name only if user hasn't manually edited it
    if (!editMode && !isNameManuallyEdited) {
      // Remove special characters and convert spaces to underscores
      let autoName = value.trim().toLowerCase().replace(/\s+/g, "_");
      autoName = autoName.replace(/[^a-z0-9_]/g, "");
      // Replace multiple consecutive underscores with a single underscore
      autoName = autoName.replace(/_+/g, "_");
      // Remove leading or trailing underscores
      autoName = autoName.replace(/^_+|_+$/g, "");
      // Remove leading numbers (must start with letter or underscore)
      autoName = autoName.replace(/^[0-9]+/, "");
      setListName(autoName);
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    // Convert to lowercase and remove special characters - only allow letters, numbers, and underscores
    let sanitized = value.toLowerCase().replace(/[^a-z0-9_]/g, "");

    // Replace multiple consecutive underscores with a single underscore
    sanitized = sanitized.replace(/_+/g, "_");

    // Remove leading or trailing underscores
    sanitized = sanitized.replace(/^_+|_+$/g, "");

    // Remove leading numbers (must start with letter - like variable naming)
    sanitized = sanitized.replace(/^[0-9]+/, "");

    setListName(sanitized);
    // Mark that user has manually edited the name
    setIsNameManuallyEdited(true);
  };

  const handlePlatformChange = (newPlatform) => {
    const newMaxFields = newPlatform === 'mobile' ? 8 : 15;

    if (selectedFields.length > newMaxFields) {
      const confirmMessage = `Switching to ${newPlatform} allows only ${newMaxFields} fields, but you have ${selectedFields.length} selected. The extra fields will be moved back to available fields. Continue?`;

      if (window.confirm(confirmMessage)) {
        const fieldsToKeep = selectedFields.slice(0, newMaxFields);
        const fieldsToRemove = selectedFields.slice(newMaxFields);

        setSelectedFields(fieldsToKeep);
        setAvailableFields([...availableFields, ...fieldsToRemove]);
        setPlatform(newPlatform);
        setError("");
      }
    } else {
      setPlatform(newPlatform);
      setError("");
    }
  };

  // Filter management functions
  const addFilter = () => {
    const newFilter = {
      id: Date.now(),
      field: "",
      operator: "equals",
      value: "",
    };
    setFilters([...filters, newFilter]);
    setShowFilterSection(true);
  };

  const removeFilter = (filterId) => {
    setFilters(filters.filter(f => f.id !== filterId));
  };

  const updateFilter = (filterId, field, value) => {
    setFilters(filters.map(f =>
      f.id === filterId ? { ...f, [field]: value } : f
    ));
  };

  const getOperatorsForField = (fieldName) => {
    const field = [...selectedFields, ...availableFields].find(f => f.name === fieldName);
    if (!field) return [];

    const dataType = field.dataType?.toLowerCase();

    switch (dataType) {
      case 'date':
      case 'datetime':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' },
          { value: 'greater_than', label: 'Greater Than' },
          { value: 'less_than', label: 'Less Than' },
          { value: 'between', label: 'Between' },
          { value: 'is_null', label: 'Is Null' },
          { value: 'is_not_null', label: 'Is Not Null' }
        ];
      case 'number':
      case 'integer':
      case 'decimal':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' },
          { value: 'greater_than', label: 'Greater Than' },
          { value: 'less_than', label: 'Less Than' },
          { value: 'between', label: 'Between' },
          { value: 'is_null', label: 'Is Null' },
          { value: 'is_not_null', label: 'Is Not Null' }
        ];
      case 'boolean':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'is_null', label: 'Is Null' },
          { value: 'is_not_null', label: 'Is Not Null' }
        ];
      default: // text, email, phone, etc.
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' },
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Not Contains' },
          { value: 'starts_with', label: 'Starts With' },
          { value: 'ends_with', label: 'Ends With' },
          { value: 'is_null', label: 'Is Null' },
          { value: 'is_not_null', label: 'Is Not Null' }
        ];
    }
  };

  const isOperatorValueRequired = (operator) => {
    return !['is_null', 'is_not_null'].includes(operator);
  };

  // Helper function to get field data type
  const getFieldDataType = (fieldName) => {
    const field = [...selectedFields, ...availableFields].find(f => f.name === fieldName);
    return field?.dataType?.toLowerCase() || 'text';
  };

  // Helper function to check if field is date/datetime
  const isDateField = (fieldName) => {
    const dataType = getFieldDataType(fieldName);
    return dataType === 'date' || dataType === 'datetime';
  };

  const handleSave = () => {
    if (!listLabel.trim() || !listName.trim()) {
      setError("Both List Label and List Name are required");
      return;
    }

    // Validate naming convention - must start with a letter (case-insensitive)
    if (!/^[a-zA-Z]/.test(listName)) {
      setError("List Name must start with a letter");
      return;
    }

    // Validate naming convention - only letters, numbers, and underscores (case-insensitive)
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/i.test(listName)) {
      setError("List Name can only contain letters, numbers, and underscores");
      return;
    }

    if (selectedFields.length === 0) {
      setError("Please select at least one field");
      return;
    }

    const maxFields = getMaxFields();
    if (selectedFields.length > maxFields) {
      setError(`Too many fields selected. Maximum ${maxFields} fields allowed for ${platform} platform`);
      return;
    }

    setError("");

    // Automatically determine view type based on whether Kanban is configured
    const hasKanbanConfig = kanbanGroupByField && kanbanGroupByField.trim() !== '';

    const config = {
      viewType: hasKanbanConfig ? 'kanban' : 'list',
      logic: filterLogic,
      fields: selectedFields.map((f) => f.name),
      sortBy: sortBy,
      sortOrder: sortOrder,
      filters: filters.filter(f => f.field && f.operator), // Only include valid filters
    };

    // Add Kanban configuration if group by field is selected
    if (hasKanbanConfig) {
      config.kanban = {
        groupByField: kanbanGroupByField,
        cardFields: kanbanCardFields,
        colorField: kanbanColorField,
        sortBy: kanbanSortBy,
        sortOrder: kanbanSortOrder,
        showRecordCount: kanbanShowRecordCount,
        summaryFields: kanbanSummaryFields
      };
    }

    const payload = {
      object_name: objectName,
      label: listLabel.trim(),
      name: listName.trim(),
      is_default: isDefault,
      platform: platform,
      shared_roles: [],
      config,
    };

    if (editMode && listViewData) {
      onUpdate(listViewData.id, payload);
    } else {
      onSave(payload);
    }
  };

  const getFieldTypeIcon = (dataType) => {
    switch (dataType?.toLowerCase()) {
      case 'date':
      case 'datetime':
        return '📅';
      case 'number':
      case 'integer':
      case 'decimal':
        return '🔢';
      case 'boolean':
        return '✅';
      case 'email':
        return '📧';
      case 'phone':
        return '📞';
      case 'url':
        return '🔗';
      default:
        return '📝';
    }
  };

  // Filter available fields based on search term
  const getFilteredAvailableFields = () => {
    if (!fieldSearchTerm.trim()) {
      return availableFields;
    }

    const searchLower = fieldSearchTerm.toLowerCase();
    return availableFields.filter(field =>
      field.label.toLowerCase().includes(searchLower) ||
      field.name.toLowerCase().includes(searchLower) ||
      field.dataType.toLowerCase().includes(searchLower)
    );
  };

  // Kanban card field management functions
  const moveKanbanFieldToSelected = (field) => {
    const newSelected = [...kanbanSelectedFields, field];
    const newAvailable = kanbanAvailableFields.filter((f) => f.id !== field.id);
    setKanbanSelectedFields(newSelected);
    setKanbanAvailableFields(newAvailable);
    setKanbanCardFields(newSelected.map(f => f.name));
  };

  const moveKanbanFieldToAvailable = (field) => {
    const newAvailable = [...kanbanAvailableFields, field];
    const newSelected = kanbanSelectedFields.filter((f) => f.id !== field.id);
    setKanbanAvailableFields(newAvailable);
    setKanbanSelectedFields(newSelected);
    setKanbanCardFields(newSelected.map(f => f.name));
  };

  const moveKanbanFieldUp = (index) => {
    if (index === 0) return;
    const newFields = [...kanbanSelectedFields];
    [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
    setKanbanSelectedFields(newFields);
    setKanbanCardFields(newFields.map(f => f.name));
  };

  const moveKanbanFieldDown = (index) => {
    if (index === kanbanSelectedFields.length - 1) return;
    const newFields = [...kanbanSelectedFields];
    [newFields[index + 1], newFields[index]] = [newFields[index], newFields[index + 1]];
    setKanbanSelectedFields(newFields);
    setKanbanCardFields(newFields.map(f => f.name));
  };

  // Kanban drag and drop functions
  const handleKanbanDragStart = (e, index) => {
    setKanbanDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleKanbanDragOver = (e, index) => {
    e.preventDefault();
    if (kanbanDraggedItem !== null && kanbanDraggedItem !== index) {
      setKanbanDragOverItem(index);
    }
  };

  const handleKanbanDragLeave = () => {
    setKanbanDragOverItem(null);
  };

  const handleKanbanDrop = (e, index) => {
    e.preventDefault();
    if (kanbanDraggedItem === null || kanbanDraggedItem === index) {
      setKanbanDraggedItem(null);
      setKanbanDragOverItem(null);
      return;
    }

    const newFields = [...kanbanSelectedFields];
    const draggedField = newFields[kanbanDraggedItem];
    newFields.splice(kanbanDraggedItem, 1);
    newFields.splice(index, 0, draggedField);

    setKanbanSelectedFields(newFields);
    setKanbanCardFields(newFields.map(f => f.name));
    setKanbanDraggedItem(null);
    setKanbanDragOverItem(null);
  };

  const handleKanbanDragEnd = () => {
    setKanbanDraggedItem(null);
    setKanbanDragOverItem(null);
  };

  // Filter Kanban available fields based on search term
  const getFilteredKanbanAvailableFields = () => {
    if (!kanbanFieldSearchTerm.trim()) {
      return kanbanAvailableFields;
    }

    const searchLower = kanbanFieldSearchTerm.toLowerCase();
    return kanbanAvailableFields.filter(field =>
      field.label.toLowerCase().includes(searchLower) ||
      field.name.toLowerCase().includes(searchLower) ||
      field.dataType.toLowerCase().includes(searchLower)
    );
  };

  // Summary fields management functions
  const addSummaryField = () => {
    const newSummaryField = {
      id: Date.now(),
      field: '',
      aggregation: 'sum'
    };
    setKanbanSummaryFields([...kanbanSummaryFields, newSummaryField]);
  };

  const removeSummaryField = (id) => {
    setKanbanSummaryFields(kanbanSummaryFields.filter(sf => sf.id !== id));
  };

  const updateSummaryField = (id, field, value) => {
    setKanbanSummaryFields(kanbanSummaryFields.map(sf =>
      sf.id === id ? { ...sf, [field]: value } : sf
    ));
  };

  // Get aggregation options based on field data type
  const getAggregationOptions = (fieldName) => {
    if (!fieldName) return [];
    const allFields = [...selectedFields, ...availableFields];
    const field = allFields.find(f => f.name === fieldName);
    if (!field) return [];

    const dataType = field.dataType?.toLowerCase();
    const numericTypes = ['number', 'integer', 'decimal', 'currency', 'rollupsummary'];
    const dateTypes = ['date', 'datetime'];

    if (numericTypes.includes(dataType)) {
      return [
        { value: 'sum', label: 'Sum' },
        { value: 'avg', label: 'Average' },
        { value: 'min', label: 'Minimum' },
        { value: 'max', label: 'Maximum' },
        { value: 'count', label: 'Count' }
      ];
    } else if (dateTypes.includes(dataType)) {
      return [
        { value: 'min', label: 'Earliest' },
        { value: 'max', label: 'Latest' },
        { value: 'count', label: 'Count' }
      ];
    } else {
      return [
        { value: 'count', label: 'Count' }
      ];
    }
  };

  // Get numeric fields for summary (fields that support sum/avg/min/max)
  const getNumericFieldsForSummary = () => {
    const allFields = [...selectedFields, ...availableFields];
    return allFields.filter(f => {
      const dataType = f.dataType?.toLowerCase();
      return ['number', 'integer', 'decimal', 'currency', 'rollupsummary', 'date', 'datetime'].includes(dataType);
    });
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
      dialogClassName="enhanced-list-view-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="d-flex align-items-center">
            {editMode ? (
              <>
                <FaEdit className="me-2" />
                Edit List View
              </>
            ) : listViewData ? (
              <>
                <FaClone className="me-2" />
                Clone List View
              </>
            ) : (
              <>
                <FaPlus className="me-2" />
                Create New List View
              </>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading fields...</p>
          </div>
        ) : (
          <>
            {/* Basic Information */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="modal-theme-header modal-header">
                <h6 className="mb-0 text-white" style={{ color: "pink" }}>
                  <FaCog className="me-2" />
                  Basic Information
                </h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">List Label</Form.Label>
                      <Form.Control
                        type="text"
                        value={listLabel}
                        onChange={handleLabelChange}
                        placeholder="e.g. My Customers"
                        className="border-0 bg-light"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        List Name (API Name)
                        <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={listName}
                        onChange={handleNameChange}
                        placeholder="e.g. my_customers_mobile"
                        className="border-0 bg-light"
                      />
                      <Form.Text className="text-muted">
                        Must start with a letter, can contain letters, numbers, and underscores (automatically converted to lowercase)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Sort By</Form.Label>
                      <Form.Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="border-0 bg-light"
                      >
                        {selectedFields.map(field => (
                          <option key={field.name} value={field.name}>
                            {field.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Sort Order</Form.Label>
                      <Form.Select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        className="border-0 bg-light"
                      >
                        <option value="ASC">Ascending</option>
                        <option value="DESC">Descending</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Platform</Form.Label>
                      <Form.Select
                        value={platform}
                        onChange={(e) => handlePlatformChange(e.target.value)}
                        className="border-0 bg-light"
                      >
                        <option value="web">Web</option>
                        <option value="mobile">Mobile</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Select which platform this list view should be displayed on
                        <br />
                        <strong>Field Limits:</strong> Web (15 fields) • Mobile (8 fields)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-0 d-flex align-items-end" style={{ height: '100%' }}>
                      <Form.Check
                        type="checkbox"
                        label={
                          <span>
                            <FaStar className="me-2 text-warning" />
                            Set as default list view for {platform}
                          </span>
                        }
                        checked={isDefault}
                        onChange={(e) => setIsDefault(e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Kanban Configuration */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="modal-theme-header modal-header">
                <h6 className="mb-0 text-white">
                  <FaCog className="me-2" />
                  Kanban Configuration
                </h6>
                <small className="text-white">Optional: Enable Kanban board view for this list</small>
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="mb-3">
                  <small>
                    <strong>💡 Tip:</strong> Configure a Kanban view by selecting a picklist field to group records by.
                    Once configured, you can switch between List and Kanban views using the view switcher.
                    Leave the "Group By Field" empty if you don't need Kanban view for this list.
                  </small>
                </Alert>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Group By Field (Picklist)
                      </Form.Label>
                      <Form.Select
                        value={kanbanGroupByField}
                        onChange={(e) => setKanbanGroupByField(e.target.value)}
                        className="border-0 bg-light"
                      >
                        <option value="">None - Kanban view disabled</option>
                        {[...selectedFields, ...availableFields]
                          .filter(f => f.dataType === 'picklist')
                          .map(field => (
                            <option key={field.name} value={field.name}>
                              {field.label}
                            </option>
                          ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Select a picklist field to enable Kanban view (creates columns for each value)
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Color Field</Form.Label>
                      <Form.Select
                        value={kanbanColorField}
                        onChange={(e) => setKanbanColorField(e.target.value)}
                        className="border-0 bg-light"
                        disabled={!kanbanGroupByField}
                      >
                        <option value="">None (Default)</option>
                        {selectedFields.map(field => (
                          <option key={field.name} value={field.name}>
                            {field.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Optional: Color code cards based on this field's value
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Sort Cards By</Form.Label>
                      <Form.Select
                        value={kanbanSortBy}
                        onChange={(e) => setKanbanSortBy(e.target.value)}
                        className="border-0 bg-light"
                        disabled={!kanbanGroupByField}
                      >
                        <option value="">None (Default Order)</option>
                        {selectedFields.map(field => (
                          <option key={field.name} value={field.name}>
                            {field.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Optional: Sort cards within each column by this field
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Sort Order</Form.Label>
                      <Form.Select
                        value={kanbanSortOrder}
                        onChange={(e) => setKanbanSortOrder(e.target.value)}
                        className="border-0 bg-light"
                        disabled={!kanbanSortBy || !kanbanGroupByField}
                      >
                        <option value="asc">Ascending</option>
                        <option value="desc">Descending</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold d-flex align-items-center justify-content-between">
                    <span>Fields to Display on Cards</span>
                    {!kanbanGroupByField && (
                      <Badge bg="secondary" className="ms-2">Select Group By Field first</Badge>
                    )}
                  </Form.Label>
                  <Form.Text className="text-muted d-block mb-2">
                    Select and order fields that will appear on each Kanban card. Drag fields to reorder them.
                  </Form.Text>

                  {kanbanGroupByField ? (
                    <Row>
                      <Col md={5}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">
                            <Badge bg="secondary" className="me-2">
                              {getFilteredKanbanAvailableFields().length}
                            </Badge>
                            Available Fields
                          </h6>
                        </div>

                        {/* Search Input for Available Kanban Fields */}
                        <div className="mb-3">
                          <div className="field-search-container">
                            <FaSearch className="search-icon" />
                            <Form.Control
                              type="text"
                              placeholder="Search fields by name, label, or type..."
                              value={kanbanFieldSearchTerm}
                              onChange={(e) => setKanbanFieldSearchTerm(e.target.value)}
                              className="field-search-input"
                              style={{
                                paddingLeft: '36px',
                                paddingRight: kanbanFieldSearchTerm ? '36px' : '12px'
                              }}
                            />
                            {kanbanFieldSearchTerm && (
                              <button
                                type="button"
                                onClick={() => setKanbanFieldSearchTerm("")}
                                className="clear-search-btn"
                                title="Clear search"
                              >
                                <FaTimes size={12} />
                              </button>
                            )}
                          </div>
                          {kanbanFieldSearchTerm && (
                            <div className="search-results-info">
                              Showing {getFilteredKanbanAvailableFields().length} of {kanbanAvailableFields.length} fields
                            </div>
                          )}
                        </div>
                        <div
                          className="border rounded p-3"
                          style={{ maxHeight: "400px", overflowY: "auto", backgroundColor: "#f8f9fa" }}
                        >
                          {getFilteredKanbanAvailableFields().length === 0 ? (
                            <div className="text-center text-muted py-4">
                              <FaEye className="mb-2" size={24} />
                              <p>
                                {kanbanFieldSearchTerm.trim()
                                  ? `No fields found matching "${kanbanFieldSearchTerm}"`
                                  : "All fields have been selected"}
                              </p>
                            </div>
                          ) : (
                            getFilteredKanbanAvailableFields().map((field) => (
                              <div
                                key={field.id}
                                className="d-flex align-items-center justify-content-between p-2 mb-2 bg-white rounded"
                                style={{
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  border: "1px solid #dee2e6",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#e3f2fd";
                                  e.currentTarget.style.borderColor = "#0176d3";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "white";
                                  e.currentTarget.style.borderColor = "#dee2e6";
                                }}
                                onClick={() => moveKanbanFieldToSelected(field)}
                              >
                                <div className="d-flex align-items-center">
                                  <span className="me-2" style={{ fontSize: '18px' }}>{getFieldTypeIcon(field.dataType)}</span>
                                  <div>
                                    <div style={{ fontWeight: "500", fontSize: '14px' }}>{field.label}</div>
                                    <small className="text-muted" style={{ fontSize: '12px' }}>{field.name}</small>
                                  </div>
                                </div>
                                <FaArrowRight className="text-primary" />
                              </div>
                            ))
                          )}
                        </div>
                      </Col>

                      <Col md={2}></Col>

                      <Col md={5}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">
                            <Badge bg="primary" className="me-2">
                              {kanbanSelectedFields.length}
                            </Badge>
                            Card Fields
                          </h6>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted d-block" style={{ height: '20px', lineHeight: '20px' }}>
                            Drag to reorder • Click <FaTimes size={10} className="text-danger" /> to remove
                          </small>
                          {kanbanFieldSearchTerm && (
                            <div style={{ height: '24px' }}>
                              {/* Spacer to align with search results info */}
                            </div>
                          )}
                        </div>
                        <div
                          className="border rounded p-3"
                          style={{ maxHeight: "400px", overflowY: "auto", backgroundColor: "#f8f9fa" }}
                        >
                          {kanbanSelectedFields.length === 0 ? (
                            <div className="text-center text-muted py-4">
                              <FaArrowLeft className="mb-2" size={24} />
                              <p className="mb-0">No fields selected</p>
                              <small>Click fields from the left to add them</small>
                            </div>
                          ) : (
                            kanbanSelectedFields.map((field, index) => (
                              <div
                                key={field.id}
                                draggable
                                onDragStart={(e) => handleKanbanDragStart(e, index)}
                                onDragOver={(e) => handleKanbanDragOver(e, index)}
                                onDragLeave={handleKanbanDragLeave}
                                onDrop={(e) => handleKanbanDrop(e, index)}
                                onDragEnd={handleKanbanDragEnd}
                                className="d-flex align-items-center justify-content-between p-2 mb-2 bg-white rounded"
                                style={{
                                  cursor: "move",
                                  transition: "all 0.2s",
                                  border: kanbanDragOverItem === index ? "2px dashed #0176d3" : "1px solid #dee2e6",
                                  opacity: kanbanDraggedItem === index ? 0.5 : 1,
                                  backgroundColor: kanbanDragOverItem === index ? "#e3f2fd" : "white",
                                }}
                              >
                                <div className="d-flex align-items-center flex-grow-1">
                                  <FaGripVertical className="text-muted me-2" style={{ cursor: 'grab' }} />
                                  <span className="me-2" style={{ fontSize: '18px' }}>{getFieldTypeIcon(field.dataType)}</span>
                                  <div className="flex-grow-1">
                                    <div style={{ fontWeight: "500", fontSize: '14px' }}>{field.label}</div>
                                    <small className="text-muted" style={{ fontSize: '12px' }}>{field.name}</small>
                                  </div>
                                </div>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-danger"
                                  onClick={() => moveKanbanFieldToAvailable(field)}
                                  style={{ minWidth: 'auto' }}
                                >
                                  <FaTrash size={14} />
                                </Button>
                              </div>
                            ))
                          )}
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <div className="border rounded p-4 text-center text-muted bg-light" style={{ fontSize: '13px' }}>
                      <FaCog size={24} className="mb-2" style={{ opacity: 0.5 }} />
                      <p className="mb-0">Please select a "Group By Field" first to enable card field selection</p>
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Show record count in column headers"
                    checked={kanbanShowRecordCount}
                    onChange={(e) => setKanbanShowRecordCount(e.target.checked)}
                    disabled={!kanbanGroupByField}
                  />
                </Form.Group>

                {/* Summary Fields Configuration */}
                <Form.Group className="mb-0">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Form.Label className="fw-bold mb-0">Summary Fields (Column Footers)</Form.Label>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={addSummaryField}
                      disabled={!kanbanGroupByField}
                    >
                      <FaPlus className="me-1" />
                      Add Summary
                    </Button>
                  </div>
                  <Form.Text className="text-muted d-block mb-2">
                    Configure fields to display aggregated values (sum, avg, count, etc.) in column footers
                  </Form.Text>

                  {kanbanSummaryFields.length === 0 ? (
                    <div className="border rounded p-3 text-center text-muted bg-light" style={{ fontSize: '13px' }}>
                      <FaCog size={20} className="mb-2" style={{ opacity: 0.5 }} />
                      <p className="mb-0">No summary fields configured</p>
                      <small>Click "Add Summary" to add aggregated field values</small>
                    </div>
                  ) : (
                    <div className="border rounded p-3 bg-light">
                      {kanbanSummaryFields.map((summaryField, index) => (
                        <div key={summaryField.id} className="d-flex align-items-center gap-2 mb-2 bg-white p-2 rounded border">
                          <div className="flex-grow-1">
                            <Row>
                              <Col md={6}>
                                <Form.Select
                                  value={summaryField.field}
                                  onChange={(e) => updateSummaryField(summaryField.id, 'field', e.target.value)}
                                  className="border-0 bg-light"
                                  size="sm"
                                >
                                  <option value="">Select Field</option>
                                  {getNumericFieldsForSummary().map(field => (
                                    <option key={field.name} value={field.name}>
                                      {field.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Col>
                              <Col md={5}>
                                <Form.Select
                                  value={summaryField.aggregation}
                                  onChange={(e) => updateSummaryField(summaryField.id, 'aggregation', e.target.value)}
                                  className="border-0 bg-light"
                                  size="sm"
                                  disabled={!summaryField.field}
                                >
                                  {summaryField.field ? (
                                    getAggregationOptions(summaryField.field).map(opt => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))
                                  ) : (
                                    <option value="">Select field first</option>
                                  )}
                                </Form.Select>
                              </Col>
                            </Row>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeSummaryField(summaryField.id)}
                            style={{ minWidth: 'auto', padding: '4px 8px' }}
                          >
                            <FaTrash size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Field Selection */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="modal-theme-header modal-header">
                <h6 className="mb-0 text-white">
                  <FaEye className="me-2" />
                  Field Selection
                </h6>
                <small className="text-white">
                  Drag and drop fields to customize your list view
                </small>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={5}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        <Badge bg="secondary" className="me-2">
                          {getFilteredAvailableFields().length}
                        </Badge>
                        Available Fields
                      </h6>
                    </div>

                    {/* Search Input for Available Fields */}
                    <div className="mb-3">
                      <div className="field-search-container">
                        <FaSearch className="search-icon" />
                        <Form.Control
                          type="text"
                          placeholder="Search fields by name, label, or type..."
                          value={fieldSearchTerm}
                          onChange={(e) => setFieldSearchTerm(e.target.value)}
                          className="field-search-input"
                          style={{
                            paddingLeft: '36px',
                            paddingRight: fieldSearchTerm ? '36px' : '12px'
                          }}
                        />
                        {fieldSearchTerm && (
                          <button
                            type="button"
                            onClick={() => setFieldSearchTerm("")}
                            className="clear-search-btn"
                            title="Clear search"
                          >
                            <FaTimes size={12} />
                          </button>
                        )}
                      </div>
                      {fieldSearchTerm && (
                        <div className="search-results-info">
                          Showing {getFilteredAvailableFields().length} of {availableFields.length} fields
                        </div>
                      )}
                    </div>
                    <div
                      className="border rounded p-3"
                      style={{ maxHeight: "400px", overflowY: "auto", backgroundColor: "#f8f9fa" }}
                    >
                      {getFilteredAvailableFields().length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <FaEye className="mb-2" size={24} />
                          <p>
                            {fieldSearchTerm.trim()
                              ? `No fields found matching "${fieldSearchTerm}"`
                              : "No available fields"
                            }
                          </p>
                        </div>
                      ) : (
                        getFilteredAvailableFields().map((field) => (
                          <div
                            key={field.id}
                            className="d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={() => moveToSelected(field)}
                          >
                            <div className="d-flex align-items-center">
                              <span className="me-2">{getFieldTypeIcon(field.dataType)}</span>
                              <div>
                                <div className="fw-bold">{field.label}</div>
                                <small className="text-muted">{field.name}</small>
                              </div>
                            </div>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveToSelected(field);
                              }}
                            >
                              <FaArrowRight />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </Col>

                  <Col md={7}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        <Badge
                          bg={selectedFields.length >= getMaxFields() ? "danger" : "primary"}
                          className="me-2"
                        >
                          {selectedFields.length} / {getMaxFields()}
                        </Badge>
                        Selected Fields
                      </h6>
                      <small className="text-muted">
                        Drag to reorder • Click to remove
                      </small>
                    </div>
                    {selectedFields.length >= getMaxFields() && (
                      <Alert variant="warning" className="py-2 px-3 mb-2" style={{ fontSize: '13px' }}>
                        <strong>Limit Reached:</strong> Maximum {getMaxFields()} fields for {platform} platform
                      </Alert>
                    )}
                    <div
                      className="border rounded p-3"
                      style={{ maxHeight: "400px", overflowY: "auto", backgroundColor: "#f8f9fa" }}
                    >
                      {selectedFields.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <FaFilter className="mb-2" size={24} />
                          <p>No fields selected</p>
                          <small>Select fields from the left panel</small>
                        </div>
                      ) : (
                        selectedFields.map((field, index) => (
                          <div
                            key={field.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`d-flex justify-content-between align-items-center p-3 mb-2 bg-white rounded border ${draggedItem === index ? 'opacity-50 shadow-lg' : ''
                              } ${dragOverItem === index ? 'border-primary border-2' : ''
                              }`}
                            style={{
                              cursor: 'grab',
                              transform: draggedItem === index ? 'rotate(2deg)' : 'none',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <FaGripVertical
                                className="me-2 text-muted"
                                style={{ cursor: 'grab' }}
                              />
                              <span className="me-2">{getFieldTypeIcon(field.dataType)}</span>
                              <div>
                                <div className="fw-bold">{field.label}</div>
                                <small className="text-muted">{field.name}</small>
                              </div>
                            </div>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => moveToAvailable(field)}
                                title="Remove Field"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Filter Configuration */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="modal-theme-header modal-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 text-white">
                    <FaFilter className="me-2" />
                    Filter Configuration
                  </h6>
                  <Button
                    className="text-white"
                    variant="outline-primary"
                    size="sm"
                    onClick={addFilter}
                  >
                    <FaPlus className="me-2" />
                    Add Filter
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {filters.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <FaFilter className="mb-2" size={24} />
                    <p>No filters configured</p>
                    <small>Click "Add Filter" to create filtering rules</small>
                  </div>
                ) : (
                  <>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Filter Logic</Form.Label>
                          <Form.Select
                            value={filterLogic}
                            onChange={(e) => setFilterLogic(e.target.value)}
                            className="border-0 bg-light"
                          >
                            <option value="AND">AND (All conditions must be true)</option>
                            <option value="OR">OR (Any condition can be true)</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="filters-container">
                      {filters.map((filter, index) => (
                        <div key={filter.id} className="filter-row border rounded p-3 mb-3 bg-light">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="mb-0">Filter {index + 1}</h6>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeFilter(filter.id)}
                            >
                              <FaTrash />
                            </Button>
                          </div>

                          <Row>
                            <Col md={4}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">Field</Form.Label>
                                <Form.Select
                                  value={filter.field}
                                  onChange={(e) => updateFilter(filter.id, 'field', e.target.value)}
                                  className="border-0 bg-white"
                                >
                                  <option value="">Select Field</option>
                                  {[...selectedFields, ...availableFields].map(field => (
                                    <option key={field.name} value={field.name}>
                                      {field.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">Operator</Form.Label>
                                <Form.Select
                                  value={filter.operator}
                                  onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                                  className="border-0 bg-white"
                                  disabled={!filter.field}
                                >
                                  <option value="">Select Operator</option>
                                  {filter.field && getOperatorsForField(filter.field).map(op => (
                                    <option key={op.value} value={op.value}>
                                      {op.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold d-flex align-items-center">
                                  Value
                                  {filter.field && isDateField(filter.field) && isOperatorValueRequired(filter.operator) && (
                                    <FaQuestionCircle
                                      className="ms-2 text-primary"
                                      size={12}
                                      style={{ cursor: 'pointer' }}
                                      title="Date Filter Help"
                                      onClick={() => setShowDateHelpModal(true)}
                                    />
                                  )}
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  value={filter.value}
                                  onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                                  placeholder={isOperatorValueRequired(filter.operator) ? "Enter value" : "Not required"}
                                  className="border-0 bg-white"
                                  disabled={!isOperatorValueRequired(filter.operator)}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading || !listLabel.trim() || !listName.trim() || selectedFields.length === 0}
        >
          {editMode ? "Update List View" : listViewData ? "Clone List View" : "Create List View"}
        </Button>
      </Modal.Footer>

      {/* Date Filter Help Modal */}
      <Modal show={showDateHelpModal} onHide={() => setShowDateHelpModal(false)} size="lg" centered className="date-help-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaQuestionCircle className="me-2 text-primary" />
            Date Filter Help
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-primary mb-3">📅 Date Formats</h6>
              <div className="mb-3">
                <strong>Specific Dates:</strong>
                <ul className="mt-2">
                  <li><code>2024-01-15</code> - January 15, 2024</li>
                  <li><code>2024-12-31</code> - December 31, 2024</li>
                </ul>
              </div>

              <div className="mb-3">
                <strong>Between Operator:</strong>
                <ul className="mt-2">
                  <li><code>2024-01-01|2024-01-31</code> - January 2024</li>
                  <li><code>2024-06-01|2024-06-30</code> - June 2024</li>
                </ul>
              </div>
            </div>

            <div className="col-md-6">
              <h6 className="text-primary mb-3">🎯 Date Literals</h6>
              <div className="mb-3">
                <strong>Basic Literals:</strong>
                <ul className="mt-2">
                  <li><code>TODAY</code> - Current date</li>
                  <li><code>YESTERDAY</code> - Previous day</li>
                  <li><code>TOMORROW</code> - Next day</li>
                </ul>
              </div>

              <div className="mb-3">
                <strong>Week Literals:</strong>
                <ul className="mt-2">
                  <li><code>THIS_WEEK</code> - Current week</li>
                  <li><code>LAST_WEEK</code> - Previous week</li>
                  <li><code>NEXT_WEEK</code> - Next week</li>
                </ul>
              </div>

              <div className="mb-3">
                <strong>Month Literals:</strong>
                <ul className="mt-2">
                  <li><code>THIS_MONTH</code> - Current month</li>
                  <li><code>LAST_MONTH</code> - Previous month</li>
                  <li><code>NEXT_MONTH</code> - Next month</li>
                </ul>
              </div>

              <div className="mb-3">
                <strong>Quarter Literals:</strong>
                <ul className="mt-2">
                  <li><code>THIS_QUARTER</code> - Current quarter</li>
                  <li><code>LAST_QUARTER</code> - Previous quarter</li>
                  <li><code>NEXT_QUARTER</code> - Next quarter</li>
                </ul>
              </div>

              <div className="mb-3">
                <strong>Year Literals:</strong>
                <ul className="mt-2">
                  <li><code>THIS_YEAR</code> - Current year</li>
                  <li><code>LAST_YEAR</code> - Previous year</li>
                  <li><code>NEXT_YEAR</code> - Next year</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-12">
              <h6 className="text-primary mb-3">🔢 N-Period Literals</h6>
              <div className="row">
                <div className="col-md-4">
                  <strong>Days:</strong>
                  <ul className="mt-2">
                    <li><code>LAST_N_DAYS:7</code> - Last 7 days</li>
                    <li><code>LAST_N_DAYS:30</code> - Last 30 days</li>
                    <li><code>NEXT_N_DAYS:7</code> - Next 7 days</li>
                    <li><code>NEXT_N_DAYS:30</code> - Next 30 days</li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <strong>Weeks:</strong>
                  <ul className="mt-2">
                    <li><code>LAST_N_WEEKS:2</code> - Last 2 weeks</li>
                    <li><code>LAST_N_WEEKS:4</code> - Last 4 weeks</li>
                    <li><code>NEXT_N_WEEKS:2</code> - Next 2 weeks</li>
                    <li><code>NEXT_N_WEEKS:4</code> - Next 4 weeks</li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <strong>Months/Years:</strong>
                  <ul className="mt-2">
                    <li><code>LAST_N_MONTHS:3</code> - Last 3 months</li>
                    <li><code>LAST_N_MONTHS:6</code> - Last 6 months</li>
                    <li><code>LAST_N_YEARS:1</code> - Last 1 year</li>
                    <li><code>LAST_N_YEARS:2</code> - Last 2 years</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="alert alert-info mt-3">
            <strong>💡 Tips:</strong>
            <ul className="mb-0 mt-2">
              <li>Use specific dates (YYYY-MM-DD) for exact date matching</li>
              <li>Use date literals for relative date filtering</li>
              <li>For "Between" operator, use format: <code>start_date|end_date</code></li>
              <li>All date literals are case-insensitive</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDateHelpModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default EnhancedListViewDialog;
